import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlayerStore } from '@/store/player-store';
import { useGameStore } from '@/store/game-store';
import GameModeSelector from '@/components/GameModeSelector';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import type { GameMode } from '@/types';

interface LobbyPageProps {
  onNavigate: (page: 'home' | 'game') => void;
}

export default function LobbyPage({ onNavigate }: LobbyPageProps) {
  const { currentProfile } = usePlayerStore();
  const { initializeGame } = useGameStore();
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelectMode = (mode: GameMode) => {
    if (!currentProfile) {
      setError('请先选择玩家档案');
      return;
    }

    initializeGame(mode, {
      id: currentProfile.id,
      name: currentProfile.name,
      avatar: currentProfile.avatar,
    });

    onNavigate('game');
  };

  if (!currentProfile) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <p className="text-white text-lg mb-4">请先创建或选择玩家档案</p>
          <Button onClick={() => onNavigate('home')}>返回首页</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* 顶部导航 */}
      <nav className="p-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onNavigate('home')}
          className="text-white hover:bg-slate-800"
        >
          <ArrowLeft className="w-6 h-6" />
        </Button>
        <h1 className="text-xl font-bold text-white">游戏大厅</h1>
      </nav>

      {/* 主内容 */}
      <main className="container mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {!showModeSelector ? (
            <motion.div
              key="lobby-menu"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-2xl mx-auto"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-white mb-2">选择游戏模式</h2>
                <p className="text-slate-400">选择适合你的游戏模式开始对战</p>
              </div>

              <div className="grid gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowModeSelector(true)}
                  className="p-6 bg-gradient-to-r from-amber-600 to-amber-700 rounded-xl text-left hover:shadow-lg hover:shadow-amber-500/20 transition-all"
                >
                  <h3 className="text-2xl font-bold text-white mb-2">快速开始</h3>
                  <p className="text-amber-100">选择游戏模式，立即开始对战</p>
                </motion.button>

                <div className="grid grid-cols-2 gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl text-left hover:bg-slate-800 transition-all"
                  >
                    <h3 className="text-lg font-bold text-white mb-1">练习模式</h3>
                    <p className="text-slate-400 text-sm">与简单 AI 练习技巧</p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl text-left hover:bg-slate-800 transition-all"
                  >
                    <h3 className="text-lg font-bold text-white mb-1">挑战模式</h3>
                    <p className="text-slate-400 text-sm">击败高级 AI</p>
                  </motion.button>
                </div>
              </div>

              {/* 玩家信息卡片 */}
              <div className="mt-8 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                    <span className="text-white text-xl">♠</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{currentProfile.name}</p>
                    <p className="text-slate-400 text-sm">当前筹码: {currentProfile.chips.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="mode-selector"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-4xl mx-auto"
            >
              <Button
                variant="ghost"
                onClick={() => setShowModeSelector(false)}
                className="mb-4 text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                返回
              </Button>

              <GameModeSelector
                onSelectMode={handleSelectMode}
                currentChips={currentProfile.chips}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* 错误提示 */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="fixed top-20 left-1/2 -translate-x-1/2 bg-red-500/90 text-white px-6 py-3 rounded-lg shadow-lg"
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
